import { FileUploader } from 'api/endpoints/DigitizationUtility';
import Dexie from 'dexie';

// Create a new database with a different name to avoid migration issues
export const db = new Dexie('offlineDBv3');

// Define database schema with proper table definitions
db.version(1).stores({
  // Store for form definitions - formType is the primary key
  formDefinitions: 'formType',

  // Store for form submissions
  formSubmissions: '++id, formType, synced',

  // Store for farmer data
  farmers: '++id, name, contact, village, createdAt, updatedAt, synced',

  // Store for farm/cultivation data
  // Note: Added 'farmer' to the index list to fix the query error
  farms:
    '++id, farmerId, farmer, location, area, crop, cultivationDate, harvestDate, synced',
  cultivations: '++id, farmId, farm, cropType, startDate, endDate, synced',

  // Store for app configuration
  appConfig: 'key, value, updatedAt',

  // Store for dropdown options - with explicit primary key
  dropdownOptions: 'key',
  fileStorage:
    '++id, fileKey, formId, formType, fileName, fileType, dataUrl, createdAt',

  mapTiles: 'url, data, timestamp, z, x, y'
});

// Helper functions for working with the database
const offlineDBService = {
  // Initialize the database
  async init() {
    try {
      await db.open();
      // console.log('Offline database initialized: v2');
      return true;
    } catch (error) {
      // console.error('Failed to initialize offline database:', error);
      return false;
    }
  },

  // Save a form definition for offline use
  async saveFormDefinition(formType, formData) {
    try {
      console.log(`Saving ${formType} form definition`, formData);

      await db.formDefinitions.put({
        formType,
        formData,
        updatedAt: new Date().toISOString()
      });

      // console.log(`Form definition saved for ${formType}`);
      return true;
    } catch (error) {
      // console.error(`Error saving ${formType} form definition:`, error);
      return false;
    }
  },

  // Get a form definition
  async getFormDefinition(formType) {
    try {
      // console.log(`Getting ${formType} form definition`);
      const formDef = await db.formDefinitions.get(formType);
      // console.log(`Form definition retrieved for ${formType}:`, !!formDef);

      // if (formDef) {
      //   console.log(`Form data exists: ${!!formDef.formData}`);
      //   console.log(
      //     `Form data keys: ${
      //       formDef.formData ? Object.keys(formDef.formData).length : 0
      //     }`
      //   );
      // }

      return formDef;
    } catch (error) {
      // console.error(`Error getting ${formType} form definition:`, error);
      return null;
    }
  },

  // Save dropdown data
  async saveDropdownData(key, data) {
    try {
      // console.log(`Saving dropdown data with key: ${key}`);

      // Create a proper record with the key as primary key
      const record = {
        key: key,
        data: data,
        updatedAt: new Date().toISOString()
      };

      // Use put with single parameter when your schema has a keyPath
      await db.dropdownOptions.put(record);

      // console.log(`Dropdown data saved with key: ${key}`);
      return true;
    } catch (error) {
      // console.error(`Error saving dropdown data with key ${key}:`, error);

      // Try alternative approach if the above fails
      try {
        // console.log('Trying alternative approach to save dropdown data...');

        // Try using add instead of put (forces a new record)
        await db.dropdownOptions.delete(key);
        await db.dropdownOptions.add({
          key: key,
          data: data,
          updatedAt: new Date().toISOString()
        });

        // console.log('Alternative approach successful');
        return true;
      } catch (fallbackError) {
        // console.error('Alternative approach also failed:', fallbackError);
        return false;
      }
    }
  },

  // Get dropdown data with improved error handling
  async getDropdownData(key = 'dropdownOptions') {
    try {
      // console.log(`Getting dropdown data for key: ${key}`);

      // Get the record by key
      const record = await db.dropdownOptions.get(key);

      if (!record) {
        // console.log(`No dropdown data found for key: ${key}`);
        return null;
      }

      // console.log(`Dropdown data retrieved for key: ${key}`);
      return record.data; // Return the data property of the record
    } catch (error) {
      // console.error(`Error retrieving dropdown data for key ${key}:`, error);
      return null;
    }
  },

  // Debug function to examine all dropdown data
  async debugDropdownData() {
    try {
      const allRecords = await db.dropdownOptions.toArray();
      // console.log(`Found ${allRecords.length} dropdown records in database`);

      allRecords.forEach((record, index) => {
        if (record.data) {
          console.log('- Data keys:', Object.keys(record.data));
        }
      });

      return allRecords;
    } catch (error) {
      console.error('Error debugging dropdown data:', error);
      return [];
    }
  },

  // Clear all local data
  async clearAllData() {
    try {
      // console.log('Clearing all local data');

      await db.formDefinitions.clear();
      await db.formSubmissions.clear();
      await db.farmers.clear();
      await db.farms.clear();
      await db.appConfig.clear();
      await db.dropdownOptions.clear();

      console.log('All local data cleared');
      return true;
    } catch (error) {
      console.error('Error clearing local data:', error);
      return false;
    }
  },

  // Save form submission data for later syncing
  async saveFormSubmission(formType, data) {
    try {
      // console.log(`Saving ${formType} form submission`);

      // Handle different argument patterns
      let submissionData = data;

      // If called with (formType, null, data)
      if (arguments.length === 3 && arguments[1] === null) {
        submissionData = arguments[2];
      }

      const id = await db.formSubmissions.add({
        formType,
        data: submissionData,
        timestamp: new Date().toISOString(),
        synced: false
      });

      // console.log(`Form submission saved with ID: ${id}`);
      return id;
    } catch (error) {
      console.error('Error saving form submission:', error);
      throw error;
    }
  },

  // Save farmer data
  async saveFarmer(farmerData) {
    try {
      const now = new Date().toISOString();

      // Ensure synced is explicitly set to false
      const dataToSave = {
        ...farmerData,
        createdAt: now,
        updatedAt: now,
        synced: false,
        syncAttempted: false, // Initialize sync attempt tracking
        syncError: null // Initialize sync error tracking
      };

      const id = await db.farmers.add(dataToSave);
      console.log(`Farmer saved with ID: ${id}`, dataToSave);
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));
      return id;
    } catch (error) {
      console.error('Error saving farmer:', error);
      throw error;
    }
  },

  // Delete a farmer record
  async deleteFarmer(id) {
    try {
      console.log(`Deleting farmer with ID: ${id}`);
      await db.farmers.delete(id);
      console.log(`Farmer with ID: ${id} deleted successfully`);
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));
      return true;
    } catch (error) {
      console.error(`Error deleting farmer with ID ${id}:`, error);
      throw error;
    }
  },

  // Save farm/cultivation data
  async saveFarm(farmData) {
    try {
      const id = await db.farms.add({
        ...farmData,
        createdAt: new Date().toISOString(),
        synced: false,
        syncAttempted: false,
        syncError: null
      });
      // console.log(`Farm saved with ID: ${id}`);
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));
      return id;
    } catch (error) {
      console.error('Error saving farm:', error);
      throw error;
    }
  },

  // Delete a farmer record
  async deleteFarm(id) {
    try {
      console.log(`Deleting farm with ID: ${id}`);
      await db.farms.delete(id);
      console.log(`Farmer with ID: ${id} deleted successfully`);
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));
      return true;
    } catch (error) {
      console.error(`Error deleting farm with ID ${id}:`, error);
      throw error;
    }
  },

  // Enhanced getUnsyncedData function with better logging
  async getUnsyncedData() {
    try {
      console.log('Getting unsynced data from database...');

      // For form submissions
      const forms = await db.formSubmissions
        .filter(item => item.synced === false)
        .toArray();
      console.log(`Found ${forms.length} unsynced form submissions`);

      // For farmers - explicitly filter for synced === false
      const farmers = await db.farmers
        .filter(item => item.synced === false)
        .toArray();
      console.log(`Found ${farmers.length} unsynced farmers`);

      // Additional logging
      const allFarmers = await db.farmers.toArray();
      console.log(`Total farmers in DB: ${allFarmers.length}`);
      console.log(`Farmers with synced=false: ${farmers.length}`);

      // Log sample farmer if available
      if (farmers.length > 0) {
        console.log('Sample unsynced farmer:', farmers[0]);
        console.log(
          'All unsynced farmer IDs:',
          farmers.map(f => f.id).join(', ')
        );
      }

      // For farms - explicitly filter for synced === false
      const farms = await db.farms
        .filter(item => item.synced === false)
        .toArray();
      console.log(`Found ${farms.length} unsynced farms`);

      // For cultivations - explicitly filter for synced === false
      let cultivations = [];
      try {
        // Check if cultivations table exists
        if (db.cultivations) {
          cultivations = await db.cultivations
            .filter(item => item.synced === false)
            .toArray();
          console.log(`Found ${cultivations.length} unsynced cultivations`);

          // Log sample cultivation if available
          if (cultivations.length > 0) {
            console.log('Sample unsynced cultivation:', cultivations[0]);
            console.log(
              'All unsynced cultivation IDs:',
              cultivations.map(c => c.id).join(', ')
            );
          }
        } else {
          console.log(
            'Cultivations table does not exist in the database schema'
          );
        }
      } catch (cultivationError) {
        console.error('Error getting unsynced cultivations:', cultivationError);
        cultivations = []; // Set to empty array on error
      }

      return {
        forms,
        farmers,
        farms,
        cultivations
      };
    } catch (error) {
      console.error('Error getting unsynced data:', error);
      return {
        forms: [],
        farmers: [],
        farms: [],
        cultivations: []
      };
    }
  },

  // Mark item as synced
  async markAsSynced(table, id) {
    try {
      await db[table].update(id, {
        synced: true,
        updatedAt: new Date().toISOString()
      });
      // console.log(`Item ${id} in ${table} marked as synced`);
      return true;
    } catch (error) {
      // console.error(`Error marking item ${id} in ${table} as synced:`, error);
      return false;
    }
  },

  async markAsSyncedAndRemove(table, id) {
    try {
      console.log(`Marking and removing ${table} record with ID: ${id}`);

      // Validate that the table exists
      if (!db[table]) {
        throw new Error(`Table '${table}' does not exist in the database`);
      }

      // First, verify the record exists and retrieve it
      const recordExists = await db[table].get(id);
      if (!recordExists) {
        console.log(
          `Record with ID ${id} does not exist in ${table}, nothing to delete`
        );
        return true; // Consider this a success since there's nothing to delete
      }

      // Additional safety check - if record has a syncError, log a warning
      // This helps catch cases where we're deleting records that failed to sync
      if (recordExists.syncError) {
        console.warn(
          `WARNING: Attempting to delete record ${id} from ${table} that has a sync error: "${recordExists.syncError}". This may indicate a bug in the sync logic.`
        );
      }

      // Perform a transaction to ensure consistency
      await db.transaction('rw', db[table], async () => {
        // First, mark as synced (useful for debugging and ensures we have a record of sync)
        await db[table].update(id, {
          synced: true,
          syncError: null, // Clear any previous sync errors
          updatedAt: new Date().toISOString()
        });

        // Then delete the record
        await db[table].delete(id);

        // Verify deletion
        const recordAfterDelete = await db[table].get(id);
        if (recordAfterDelete) {
          console.error(`Record still exists after deletion attempt!`);
          // Try one more time with a different approach
          await db[table].where('id').equals(id).delete();

          // Check again
          const checkAgain = await db[table].get(id);
          if (checkAgain) {
            throw new Error(
              `Failed to delete record with ID ${id} from table ${table} after multiple attempts`
            );
          }
        }
      });

      console.log(`Successfully removed ${table} record with ID: ${id}`);
      return true;
    } catch (error) {
      console.error(`Error removing ${table} record:`, error);
      // Important: Return false instead of throwing error so caller can handle this gracefully
      return false;
    }
  },

  // Update an existing farmer
  async updateFarmer(id, farmerData) {
    try {
      console.log(`Updating farmer with ID: ${id}`, farmerData);

      // Attempt to handle both numeric and string IDs
      let farmerId;

      // Try to convert to integer if it's a string containing a number
      if (typeof id === 'string' && !isNaN(id)) {
        farmerId = parseInt(id, 10);
      } else {
        farmerId = id;
      }

      // First check if the farmer exists
      const existingFarmer = await db.farmers.get(farmerId);

      if (!existingFarmer) {
        console.error(`No farmer found with ID: ${farmerId}`);

        // Try the original ID in case conversion caused issues
        const existingFarmerWithOriginalId = await db.farmers.get(id);
        if (!existingFarmerWithOriginalId) {
          console.error(`Also no farmer found with original ID: ${id}`);
          throw new Error(`No farmer found with ID: ${id}`);
        } else {
          // Use the original ID if it works
          farmerId = id;
          console.log(`Found farmer with original ID: ${id}`);
        }
      }

      // Make sure synced status remains false for offline edits
      // And handle the case where existingFarmer might not have syncError, etc.
      const updatedData = {
        ...farmerData,
        synced: false,
        updatedAt: new Date().toISOString(),
        // Safely check if these properties exist and provide defaults
        syncError:
          existingFarmer && existingFarmer.syncError !== undefined
            ? existingFarmer.syncError
            : null,
        syncAttempted:
          existingFarmer && existingFarmer.syncAttempted !== undefined
            ? existingFarmer.syncAttempted
            : false,
        lastSyncAttempt:
          existingFarmer && existingFarmer.lastSyncAttempt !== undefined
            ? existingFarmer.lastSyncAttempt
            : null
      };

      // Log what we're trying to update
      console.log(`Updating farmer with ID: ${farmerId}, data:`, updatedData);

      // Update in the database
      const result = await db.farmers.update(farmerId, updatedData);

      if (result === 0) {
        console.error(
          `Update operation affected 0 records for ID: ${farmerId}`
        );
        throw new Error(`Failed to update farmer with ID: ${farmerId}`);
      }

      console.log(
        `Successfully updated farmer with ID: ${farmerId}, affected records: ${result}`
      );
      return farmerId;
    } catch (error) {
      console.error(`Error updating farmer:`, error);
      throw error;
    }
  },

  async updateFarmerSyncStatus(id, statusUpdate) {
    try {
      console.log(`Updating sync status for farmer ${id}:`, statusUpdate);

      // Convert ID if needed
      let farmerId = id;
      if (typeof id === 'string' && !isNaN(id)) {
        farmerId = parseInt(id, 10);
      }

      // Get existing farmer
      const existingFarmer =
        (await db.farmers.get(farmerId)) || (await db.farmers.get(id));

      if (!existingFarmer) {
        console.error(`No farmer found with ID: ${id}`);
        return false;
      }

      // Update the record with provided status fields
      const result = await db.farmers.update(farmerId, {
        ...statusUpdate,
        updatedAt: new Date().toISOString()
      });

      return result > 0;
    } catch (error) {
      console.error(`Error updating farmer sync status:`, error);
      return false;
    }
  },
  // Update farmer sync error information
  async updateFarmerSyncError(id, errorMessage) {
    try {
      console.log(
        `Updating sync error for farmer with ID: ${id}`,
        errorMessage
      );

      // Attempt to handle both numeric and string IDs
      let farmerId;

      // Try to convert to integer if it's a string containing a number
      if (typeof id === 'string' && !isNaN(id)) {
        farmerId = parseInt(id, 10);
      } else {
        farmerId = id;
      }

      // First check if the farmer exists
      const existingFarmer = await db.farmers.get(farmerId);

      if (!existingFarmer) {
        console.error(`No farmer found with ID: ${farmerId}`);

        // Try the original ID in case conversion caused issues
        const existingFarmerWithOriginalId = await db.farmers.get(id);
        if (!existingFarmerWithOriginalId) {
          console.error(`Also no farmer found with original ID: ${id}`);
          throw new Error(`No farmer found with ID: ${id}`);
        } else {
          // Use the original ID if it works
          farmerId = id;
          console.log(`Found farmer with original ID: ${id}`);
        }
      }

      // Update the farmer record with error information
      const result = await db.farmers.update(farmerId, {
        syncError: errorMessage,
        syncAttempted: true,
        lastSyncAttempt: new Date().toISOString(),
        // Ensure synced remains false
        synced: false
      });

      if (result === 0) {
        console.error(
          `Update operation affected 0 records for ID: ${farmerId}`
        );
        throw new Error(`Failed to update farmer with ID: ${farmerId}`);
      }

      console.log(`Successfully updated sync error for farmer ID: ${farmerId}`);
      return farmerId;
    } catch (error) {
      console.error(`Error updating farmer sync error:`, error);
      throw error;
    }
  },

  // Get all farmers
  async getAllFarmers() {
    try {
      const farmers = await db.farmers.toArray();
      console.log(`Retrieved ${farmers.length} farmers`);
      return farmers;
    } catch (error) {
      // console.error('Error getting farmers:', error);
      return [];
    }
  },

  async getAllFarms() {
    try {
      const farms = await db.farms.toArray();
      console.log(`Retrieved ${farms.length} farms`);
      return farms;
    } catch (error) {
      // console.error('Error getting farmers:', error);
      return [];
    }
  },

  // Get farmer by ID
  async getFarmerById(id) {
    try {
      // console.log(`Getting farmer with ID: ${id}, type: ${typeof id}`);

      // Try both the original ID and parsed ID to ensure we find the record
      let farmer = null;

      // First try with the ID as-is
      farmer = await db.farmers.get(id);

      // If not found and ID is string, try parsing it as integer
      if (!farmer && typeof id === 'string' && !isNaN(id)) {
        const numericId = parseInt(id, 10);
        // console.log(`Trying again with parsed numeric ID: ${numericId}`);
        farmer = await db.farmers.get(numericId);
      }

      // If still not found and ID is numeric, try as string
      if (!farmer && typeof id === 'number') {
        const stringId = id.toString();
        // console.log(`Trying again with string ID: ${stringId}`);
        farmer = await db.farmers.get(stringId);
      }

      if (!farmer) {
        // console.warn(`No farmer found with ID: ${id}`);

        // One last attempt: try to get any farmer with this ID using where clause
        try {
          const farmers = await db.farmers.where('id').equals(id).toArray();
          if (farmers && farmers.length > 0) {
            farmer = farmers[0];
            // console.log(`Found farmer using where clause: ${farmer.id}`);
          }
        } catch (whereError) {
          console.error(`Error in where clause lookup:`, whereError);
        }

        if (!farmer) {
          return null;
        }
      }

      // console.log(`Found farmer:`, farmer);
      return farmer;
    } catch (error) {
      console.error(`Error getting farmer by ID:`, error);
      throw error;
    }
  },

  // Add this method to your offlineDBService object

  // Get farm by ID
  // Add these methods to your offlineDBService object

  // Get farm by ID
  async getFarmById(id) {
    try {
      console.log(`Getting farm with ID: ${id}, type: ${typeof id}`);

      // Try both the original ID and parsed ID to ensure we find the record
      let farm = null;

      // First try with the ID as-is
      farm = await db.farms.get(id);

      // If not found and ID is string, try parsing it as integer
      if (!farm && typeof id === 'string' && !isNaN(id)) {
        const numericId = parseInt(id, 10);
        console.log(`Trying again with parsed numeric ID: ${numericId}`);
        farm = await db.farms.get(numericId);
      }

      // If still not found and ID is numeric, try as string
      if (!farm && typeof id === 'number') {
        const stringId = id.toString();
        console.log(`Trying again with string ID: ${stringId}`);
        farm = await db.farms.get(stringId);
      }

      if (!farm) {
        console.warn(`No farm found with ID: ${id}`);

        // One last attempt: try to get any farm with this ID using where clause
        try {
          const farms = await db.farms.where('id').equals(id).toArray();
          if (farms && farms.length > 0) {
            farm = farms[0];
            console.log(`Found farm using where clause: ${farm.id}`);
          }
        } catch (whereError) {
          console.error(`Error in where clause lookup:`, whereError);
        }

        if (!farm) {
          throw new Error(`No farm found with ID: ${id}`);
        }
      }

      console.log(`Found farm:`, farm);
      return farm;
    } catch (error) {
      console.error(`Error getting farm by ID:`, error);
      throw error;
    }
  },

  // Update an existing farm
  async updateFarm(id, farmData) {
    try {
      console.log(`Updating farm with ID: ${id}`, farmData);

      // Attempt to handle both numeric and string IDs
      let farmId;

      // Try to convert to integer if it's a string containing a number
      if (typeof id === 'string' && !isNaN(id)) {
        farmId = parseInt(id, 10);
      } else {
        farmId = id;
      }

      // First check if the farm exists
      const existingFarm = await db.farms.get(farmId);

      if (!existingFarm) {
        console.error(`No farm found with ID: ${farmId}`);

        // Try the original ID in case conversion caused issues
        const existingFarmWithOriginalId = await db.farms.get(id);
        if (!existingFarmWithOriginalId) {
          console.error(`Also no farm found with original ID: ${id}`);
          throw new Error(`No farm found with ID: ${id}`);
        } else {
          // Use the original ID if it works
          farmId = id;
          console.log(`Found farm with original ID: ${id}`);
        }
      }

      // Make sure synced status remains false for offline edits
      const updatedData = {
        ...farmData,
        synced: false,
        updatedAt: new Date().toISOString(),
        // Safely check if these properties exist and provide defaults
        syncError:
          existingFarm.syncError !== undefined ? existingFarm.syncError : null,
        syncAttempted:
          existingFarm.syncAttempted !== undefined
            ? existingFarm.syncAttempted
            : false,
        lastSyncAttempt:
          existingFarm.lastSyncAttempt !== undefined
            ? existingFarm.lastSyncAttempt
            : null
      };

      // Log what we're trying to update
      console.log(`Updating farm with ID: ${farmId}, data:`, updatedData);

      // Update in the database
      const result = await db.farms.update(farmId, updatedData);

      if (result === 0) {
        console.error(`Update operation affected 0 records for ID: ${farmId}`);
        throw new Error(`Failed to update farm with ID: ${farmId}`);
      }

      console.log(
        `Successfully updated farm with ID: ${farmId}, affected records: ${result}`
      );
      return farmId;
    } catch (error) {
      console.error(`Error updating farm:`, error);
      throw error;
    }
  },

  // Update farm's farmer ID
  async updateFarmFarmerId(farmId, newFarmerId) {
    try {
      console.log(`Updating farm ${farmId} with new farmer ID: ${newFarmerId}`);

      // Get the existing farm record
      const existingFarm = await db.farms.get(farmId);

      if (!existingFarm) {
        console.error(`No farm found with ID: ${farmId}`);
        return false;
      }

      // Update the record with the new farmer ID
      // The farmer ID is stored in the 'farmer' property
      const updatedFarm = {
        ...existingFarm,
        farmer: newFarmerId, // Update the 'farmer' property
        updatedAt: new Date().toISOString()
      };

      // Log what we're trying to update
      console.log(`Updating farm with ID: ${farmId}, data:`, updatedFarm);

      // Update in the database
      const result = await db.farms.update(farmId, updatedFarm);

      if (result === 0) {
        console.error(
          `Update operation affected 0 records for farm ID: ${farmId}`
        );
        return false;
      }

      console.log(
        `Successfully updated farm ${farmId} with new farmer ID: ${newFarmerId}`
      );
      return true;
    } catch (error) {
      console.error(`Error updating farm farmer ID:`, error);
      return false;
    }
  },
  // This version doesn't rely on indexed fields

  async getFarmsByFarmerId(farmerId) {
    try {
      console.log(`Looking for farms with farmer ID: ${farmerId}`);

      // Use toArray and filter instead of indexed query
      const allFarms = await db.farms.toArray();

      // Manually filter the farms by farmer ID
      const matchingFarms = allFarms.filter(farm => {
        // Convert both to strings for comparison to avoid type issues
        const farmFarmerId = farm.farmer ? farm.farmer.toString() : null;
        const searchFarmerId = farmerId.toString();

        const isMatch = farmFarmerId === searchFarmerId;

        if (isMatch) {
          console.log(
            `Found matching farm with ID ${farm.id} for farmer ${farmerId}`
          );
        }

        return isMatch;
      });

      console.log(`Found ${matchingFarms.length} farms for farmer ${farmerId}`);
      return matchingFarms;
    } catch (error) {
      console.error(`Error getting farms for farmer ${farmerId}:`, error);
      return [];
    }
  },

  // Save config value
  async saveConfig(key, value) {
    try {
      await db.appConfig.put({
        key,
        value,
        updatedAt: new Date().toISOString()
      });
      return true;
    } catch (error) {
      // console.error(`Error saving config ${key}:`, error);
      return false;
    }
  },

  // Get config value
  async getConfig(key) {
    try {
      const config = await db.appConfig.get(key);
      return config ? config.value : null;
    } catch (error) {
      // console.error(`Error getting config ${key}:`, error);
      return null;
    }
  },

  // Check database status with enhanced details
  async checkDatabaseStatus() {
    try {
      // Count records in various tables
      const farmersCount = await db.farmers.count();
      const farmsCount = await db.farms.count();
      const formSubmissionsCount = await db.formSubmissions.count();
      const formDefinitionsCount = await db.formDefinitions.count();
      const dropdownOptionsCount = await db.dropdownOptions.count();

      // Count synced vs unsynced farmers
      const syncedFarmersCount = await db.farmers
        .where('synced')
        .equals(true)
        .count();
      const unsyncedFarmersCount = await db.farmers
        .where('synced')
        .equals(false)
        .count();

      // Get database version and open status
      const status = {
        isOpen: db.isOpen(),
        version: db.verno,
        tables: Object.keys(db._allTables),
        farmersCount,
        syncedFarmersCount,
        unsyncedFarmersCount,
        farmsCount,
        formSubmissionsCount,
        formDefinitionsCount,
        dropdownOptionsCount
      };

      // console.log('Database status:', status);
      return status;
    } catch (error) {
      // console.error('Error checking database status:', error);
      return { error: error.message };
    }
  },

  // Get pending form submissions of a specific type
  async getPendingSubmissions(formType) {
    try {
      const submissions = await db.formSubmissions
        .where('formType')
        .equals(formType)
        .filter(item => item.synced === false)
        .toArray();

      return submissions;
    } catch (error) {
      // console.error(`Error getting pending ${formType} submissions:`, error);
      return [];
    }
  },

  // Added debugging function for sync status
  async debugSyncStatus() {
    try {
      // console.log('=== DEBUG SYNC STATUS ===');

      // Count total records
      const totalFarmers = await db.farmers.count();
      // console.log(`Total farmers in DB: ${totalFarmers}`);

      // Count synced records
      const syncedFarmers = await db.farmers
        .where('synced')
        .equals(true)
        .count();
      // console.log(`Synced farmers: ${syncedFarmers}`);

      // Count unsynced records
      const unsyncedFarmers = await db.farmers
        .where('synced')
        .equals(false)
        .count();
      // console.log(`Unsynced farmers: ${unsyncedFarmers}`);

      // Get all records to check their sync status
      const allFarmers = await db.farmers.toArray();
      // console.log('All farmers sync status:');
      allFarmers.forEach(farmer => {
        console.log(
          `ID: ${farmer.id}, Name: ${farmer.first_name || 'Unknown'}, Synced: ${
            farmer.synced
          }, Error: ${farmer.syncError || 'None'}`
        );
      });

      return {
        total: totalFarmers,
        synced: syncedFarmers,
        unsynced: unsyncedFarmers,
        records: allFarmers.map(f => ({
          id: f.id,
          name: f.first_name || f.farmer_name || 'Unknown',
          synced: f.synced,
          error: f.syncError
        }))
      };
    } catch (error) {
      // console.error('Error in debugSyncStatus:', error);
      return { error: error.message };
    }
  },

  // Verify a record was successfully deleted
  async verifyRecordDeleted(table, id) {
    try {
      const record = await db[table].get(id);
      return !record; // Returns true if the record is not found (successfully deleted)
    } catch (error) {
      console.error(`Error verifying record deletion in ${table}:`, error);
      return false;
    }
  },

  async saveFile(file, metadata, formId, formType) {
    try {
      // Convert file to data URL
      const dataUrl = await this.fileToDataURL(file);

      // Generate a unique file key
      const fileKey = `file_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 10)}`;

      // Store file in IndexedDB
      const id = await db.fileStorage.add({
        fileKey,
        formId,
        formType,
        fileName: file.name,
        fileType: file.type,
        dataUrl,
        createdAt: new Date().toISOString()
      });

      // console.log('File saved with ID:', id, 'and key:', fileKey);

      return {
        id,
        fileKey,
        dataUrl
      };
    } catch (error) {
      console.error('Error saving file to IndexedDB:', error);
      throw error;
    }
  },

  /**
   * Get a file by its key
   * @param {string} fileKey - The file key
   * @returns {Promise<{dataUrl: string, fileName: string, fileType: string}|null>}
   */
  async getFile(fileKey) {
    try {
      // console.log('Getting file with key:', fileKey);

      const file = await db.fileStorage
        .where('fileKey')
        .equals(fileKey)
        .first();

      if (!file) {
        // console.warn('File not found:', fileKey);
        return null;
      }

      // console.log('File found:', file.fileName);
      return file;
    } catch (error) {
      // console.error('Error getting file:', error);
      return null;
    }
  },

  /**
   * Convert a file to a data URL
   * @param {File} file - The file to convert
   * @returns {Promise<string>} - The data URL
   */
  fileToDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  /**
   * List all files stored in the database
   * @returns {Promise<Array>} - Array of file objects
   */
  async listAllFiles() {
    try {
      return await db.fileStorage.toArray();
    } catch (error) {
      // console.error('Error listing files:', error);
      return [];
    }
  },

  /**
   * Delete a file from storage
   * @param {string} fileKey - The file key
   * @returns {Promise<boolean>} - Whether deletion was successful
   */
  async deleteFile(fileKey) {
    try {
      await db.fileStorage.where('fileKey').equals(fileKey).delete();
      // console.log('File deleted:', fileKey);
      return true;
    } catch (error) {
      // console.error('Error deleting file:', error);
      return false;
    }
  },

  async findFileKeysInData(data) {
    const fileFields = [];

    // Recursively search for file keys in the object
    const findFileKeys = (obj, prefix = '') => {
      if (!obj || typeof obj !== 'object') return;

      for (const key in obj) {
        const value = obj[key];
        const path = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string' && value.startsWith('file_')) {
          fileFields.push({ field: path, fileKey: value });
        } else if (typeof value === 'object' && value !== null) {
          // Search nested objects
          findFileKeys(value, path);
        }
      }
    };

    findFileKeys(data);

    // Check if these keys actually exist in the database
    const validatedFields = [];
    for (const field of fileFields) {
      const file = await this.getFile(field.fileKey);
      if (file) {
        validatedFields.push({ ...field, file });
      }
    }

    return validatedFields;
  },
  replaceFileKeysWithUrls(data, uploads) {
    const result = { ...data };

    for (const { field, uploadedUrl } of uploads) {
      // Handle nested fields (e.g., "profile.image")
      const fieldParts = field.split('.');

      if (fieldParts.length === 1) {
        // Simple field
        result[field] = uploadedUrl;
      } else {
        // Nested field
        let current = result;
        for (let i = 0; i < fieldParts.length - 1; i++) {
          const part = fieldParts[i];
          if (!current[part] || typeof current[part] !== 'object') {
            current[part] = {};
          }
          current = current[part];
        }
        current[fieldParts[fieldParts.length - 1]] = uploadedUrl;
      }
    }

    return result;
  },
  async uploadFileToServer(file, field) {
    try {
      // console.log(`Uploading file for field ${field}: ${file.fileName}`);

      // Convert data URL to File object
      const fileObj = this.dataURLtoFile(
        file.dataUrl,
        file.fileName,
        file.fileType
      );

      // Create fake event object
      const fakeEvent = {
        target: {
          files: [fileObj]
        }
      };

      // Define metadata for upload
      const uploadMetadata = {
        id: field,
        uploadConfig: {
          maxFileSizeMB: 5, // Adjust as needed
          uploadEndpoint: '/upload' // Adjust as needed
        }
      };

      // Use the FileUploader function to upload the file
      const result = await FileUploader(fakeEvent, uploadMetadata);

      if (!result || !result.file) {
        throw new Error('File upload failed or returned invalid result');
      }

      // console.log(
      //   `File uploaded successfully for field ${field}: ${result.file}`
      // );
      return result.file;
    } catch (error) {
      console.error(`Error uploading file for field ${field}:`, error);
      throw error;
    }
  },
  dataURLtoFile(dataURL, fileName, fileType) {
    const arr = dataURL.split(',');
    const mime = fileType || arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
  },
  // Enhanced syncData method with file upload support
  // Enhanced syncData method with file upload support and farmer ID mapping
  async syncData(apiService) {
    if (!navigator.onLine) {
      return {
        success: false,
        message: 'Device is offline'
      };
    }

    try {
      const unsyncedData = await this.getUnsyncedData();
      let syncResults = {
        forms: { total: unsyncedData.forms.length, synced: 0 },
        farmers: { total: unsyncedData.farmers.length, synced: 0 },
        farms: { total: unsyncedData.farms.length, synced: 0 },
        files: { total: 0, synced: 0, failed: 0 }
      };

      // Track all file keys that need to be processed
      const allFileKeys = new Set();

      // Track mapping from temporary farmer IDs to permanent IDs
      const farmerIdMappings = new Map();

      // Process farmers first
      for (const farmer of unsyncedData.farmers) {
        try {
          console.log(`Processing farmer ${farmer.id} for sync`);

          // Find all file keys in the farmer data
          const fileFields = await this.findFileKeysInData(farmer);

          if (fileFields.length > 0) {
            syncResults.files.total += fileFields.length;

            // Make a copy of the farmer data to modify
            let updatedFarmer = { ...farmer };
            const uploadedFiles = [];

            // Upload each file and get its URL
            for (const { field, fileKey, file } of fileFields) {
              try {
                // Track this file key
                allFileKeys.add(fileKey);

                // Upload the file to the server
                const uploadedUrl = await this.uploadFileToServer(file, field);

                // Add to the list of successfully uploaded files
                uploadedFiles.push({ field, fileKey, uploadedUrl });

                syncResults.files.synced++;
              } catch (uploadError) {
                console.error(
                  `Error uploading file ${fileKey} for field ${field}:`,
                  uploadError
                );
                syncResults.files.failed++;
              }
            }

            // Replace file keys with uploaded URLs in the farmer data
            if (uploadedFiles.length > 0) {
              updatedFarmer = this.replaceFileKeysWithUrls(
                updatedFarmer,
                uploadedFiles.map(f => ({
                  field: f.field,
                  uploadedUrl: f.uploadedUrl
                }))
              );
            }

            // Now use the updated farmer data for API submission
            try {
              const response = await apiService.saveFarmer(updatedFarmer);

              // Check response for success
              if (response && response.success === true) {
                // Get the permanent ID from the API response
                const permanentFarmerId =
                  response.data?.id ||
                  response.data?.farmer_id ||
                  response.id ||
                  response.farmerId;

                // If we have a valid permanent ID that's different from the temporary ID
                if (permanentFarmerId && permanentFarmerId !== farmer.id) {
                  console.log(
                    `Mapping temporary farmer ID ${farmer.id} to permanent ID ${permanentFarmerId}`
                  );
                  // Store the mapping for later use
                  farmerIdMappings.set(
                    farmer.id.toString(),
                    permanentFarmerId.toString()
                  );

                  // Find any farms associated with this farmer's temporary ID
                  const relatedFarms = await this.getFarmsByFarmerId(farmer.id);
                  console.log(
                    `Found ${relatedFarms.length} farms with farmer ID ${farmer.id}`
                  );

                  // Update each farm with the permanent farmer ID
                  for (const relatedFarm of relatedFarms) {
                    try {
                      // Update with permanent ID
                      await db.farms.update(relatedFarm.id, {
                        ...relatedFarm,
                        farmerId: permanentFarmerId,
                        updatedAt: new Date().toISOString()
                      });
                      console.log(
                        `Updated farm ${relatedFarm.id} with permanent farmer ID ${permanentFarmerId}`
                      );
                    } catch (updateError) {
                      console.error(
                        `Error updating farm ${relatedFarm.id} with permanent farmer ID:`,
                        updateError
                      );
                    }
                  }
                }

                // Only delete from DB if sync was successful
                const exists = await this.getFarmerById(farmer.id);
                if (exists) {
                  const deleteResult = await this.markAsSyncedAndRemove(
                    'farmers',
                    farmer.id
                  );

                  if (deleteResult) {
                    console.log(
                      `Farmer ${farmer.id} successfully synced and removed from local DB`
                    );

                    // Only delete the files if the farmer record was successfully deleted
                    for (const { fileKey } of uploadedFiles) {
                      try {
                        await this.deleteFile(fileKey);
                      } catch (deleteError) {
                        console.error(
                          `Error deleting file ${fileKey}:`,
                          deleteError
                        );
                      }
                    }
                  } else {
                    console.error(
                      `Sync succeeded but local deletion failed for farmer ${farmer.id}`
                    );
                  }
                }

                syncResults.farmers.synced++;
              } else {
                // API returned non-success response
                const errorMessage =
                  response && response.message
                    ? response.message
                    : 'API returned non-success response';

                console.error(
                  `API returned non-success for farmer ${farmer.id}:`,
                  errorMessage
                );

                // Update with error but KEEP in database
                await this.updateFarmerSyncError(farmer.id, errorMessage);
                syncResults.farmers.failed++;
              }
            } catch (apiError) {
              // API call threw an exception
              const errorMessage = apiError.message || 'API error during sync';
              console.error(
                `API error while syncing farmer ${farmer.id}:`,
                apiError
              );

              // Update with error but KEEP in database
              await this.updateFarmerSyncError(farmer.id, errorMessage);
              syncResults.farmers.failed++;
            }
          } else {
            // No file references found, proceed with normal sync
            try {
              const response = await apiService.saveFarmer(farmer);

              if (response && response.success === true) {
                // Get the permanent ID from the API response
                const permanentFarmerId =
                  response.data?.id ||
                  response.data?.farmer_id ||
                  response.id ||
                  response.farmerId;

                // If we have a valid permanent ID that's different from the temporary ID
                if (permanentFarmerId && permanentFarmerId !== farmer.id) {
                  console.log(
                    `Mapping temporary farmer ID ${farmer.id} to permanent ID ${permanentFarmerId}`
                  );
                  // Store the mapping for later use
                  farmerIdMappings.set(
                    farmer.id.toString(),
                    permanentFarmerId.toString()
                  );

                  // Find any farms associated with this farmer's temporary ID
                  const relatedFarms = await this.getFarmsByFarmerId(farmer.id);
                  console.log(
                    `Found ${relatedFarms.length} farms with farmer ID ${farmer.id}`
                  );

                  // Update each farm with the permanent farmer ID
                  for (const relatedFarm of relatedFarms) {
                    try {
                      // Update with permanent ID
                      await db.farms.update(relatedFarm.id, {
                        ...relatedFarm,
                        farmerId: permanentFarmerId,
                        updatedAt: new Date().toISOString()
                      });
                      console.log(
                        `Updated farm ${relatedFarm.id} with permanent farmer ID ${permanentFarmerId}`
                      );
                    } catch (updateError) {
                      console.error(
                        `Error updating farm ${relatedFarm.id} with permanent farmer ID:`,
                        updateError
                      );
                    }
                  }
                }

                // Only delete if sync was successful
                const deleteResult = await this.markAsSyncedAndRemove(
                  'farmers',
                  farmer.id
                );

                if (deleteResult) {
                  console.log(
                    `Farmer ${farmer.id} successfully synced and removed from local DB`
                  );
                } else {
                  console.error(
                    `Sync succeeded but local deletion failed for farmer ${farmer.id}`
                  );
                }

                syncResults.farmers.synced++;
              } else {
                // API returned non-success response
                const errorMessage =
                  response && response.message
                    ? response.message
                    : 'API returned non-success response';

                console.error(
                  `API returned non-success for farmer ${farmer.id}:`,
                  errorMessage
                );

                // Update with error but KEEP in database
                await this.updateFarmerSyncError(farmer.id, errorMessage);
                syncResults.farmers.failed++;
              }
            } catch (apiError) {
              // API call threw an exception
              const errorMessage = apiError.message || 'API error during sync';
              console.error(
                `API error while syncing farmer ${farmer.id}:`,
                apiError
              );

              // Update with error but KEEP in database
              await this.updateFarmerSyncError(farmer.id, errorMessage);
              syncResults.farmers.failed++;
            }
          }
        } catch (error) {
          // General error in processing this farmer
          console.error(`General error processing farmer ${farmer.id}:`, error);

          try {
            // Keep in database but update with error
            await this.updateFarmerSyncError(
              farmer.id,
              error.message || 'Sync processing error'
            );
          } catch (logError) {
            console.error(
              `Failed to log error for farmer ${farmer.id}:`,
              logError
            );
          }

          syncResults.farmers.failed++;
        }
      }

      // Now that we've processed all farmers and updated IDs, get fresh farm data
      // This ensures we work with the updated farmer IDs
      const refreshedFarms = await db.farms
        .filter(item => item.synced === false)
        .toArray();

      console.log(
        `Processing ${refreshedFarms.length} unsynced farms after farmer sync`
      );

      // Process farms with similar file handling
      for (const farm of refreshedFarms) {
        try {
          // Make a copy of the farm data to modify
          let farmToSync = { ...farm };

          // Check if this farm's farmer ID needs to be updated with a permanent ID
          if (
            farmToSync.farmerId &&
            farmerIdMappings.has(farmToSync.farmerId.toString())
          ) {
            const permanentFarmerId = farmerIdMappings.get(
              farmToSync.farmerId.toString()
            );
            console.log(
              `Using permanent farmer ID ${permanentFarmerId} instead of temp ID ${farmToSync.farmerId} for farm ${farm.id}`
            );
            farmToSync.farmerId = permanentFarmerId;
          }

          // Find all file keys in the farm data
          const fileFields = await this.findFileKeysInData(farmToSync);

          if (fileFields.length > 0) {
            syncResults.files.total += fileFields.length;

            // Make a copy of the farm data to modify
            const uploadedFiles = [];

            // Upload each file and get its URL
            for (const { field, fileKey, file } of fileFields) {
              try {
                // Track this file key
                allFileKeys.add(fileKey);

                // Upload the file to the server
                const uploadedUrl = await this.uploadFileToServer(file, field);

                // Add to the list of successfully uploaded files
                uploadedFiles.push({ field, fileKey, uploadedUrl });

                syncResults.files.synced++;
              } catch (uploadError) {
                console.error(
                  `Error uploading file ${fileKey} for field ${field}:`,
                  uploadError
                );
                syncResults.files.failed++;
              }
            }

            // Replace file keys with uploaded URLs in the farm data
            if (uploadedFiles.length > 0) {
              farmToSync = this.replaceFileKeysWithUrls(
                farmToSync,
                uploadedFiles.map(f => ({
                  field: f.field,
                  uploadedUrl: f.uploadedUrl
                }))
              );
            }

            // Submit the farm with updated data
            const response = await apiService.saveFarm(farmToSync);

            if (response && response.success === true) {
              await this.markAsSyncedAndRemove('farms', farm.id);

              // Delete successfully uploaded files
              for (const { fileKey } of uploadedFiles) {
                if (!allFileKeys.has(fileKey)) continue; // Skip if used by other entries

                try {
                  await this.deleteFile(fileKey);
                } catch (deleteError) {
                  console.error(`Error deleting file ${fileKey}:`, deleteError);
                }
              }

              syncResults.farms.synced++;
            } else {
              // Error handling for failed farm sync
              const errorMessage =
                response && response.message
                  ? response.message
                  : 'API returned non-success response';

              console.error(
                `API returned non-success for farm ${farm.id}:`,
                errorMessage
              );

              // Update farm record with error info
              await db.farms.update(farm.id, {
                syncError: errorMessage,
                syncAttempted: true,
                lastSyncAttempt: new Date().toISOString(),
                synced: false
              });

              syncResults.farms.failed++;
            }
          } else {
            // No file references, use normal sync
            const response = await apiService.saveFarm(farmToSync);

            if (response && response.success === true) {
              await this.markAsSyncedAndRemove('farms', farm.id);
              syncResults.farms.synced++;
            } else {
              // Error handling for failed farm sync
              const errorMessage =
                response && response.message
                  ? response.message
                  : 'API returned non-success response';

              console.error(
                `API returned non-success for farm ${farm.id}:`,
                errorMessage
              );

              // Update farm record with error info
              await db.farms.update(farm.id, {
                syncError: errorMessage,
                syncAttempted: true,
                lastSyncAttempt: new Date().toISOString(),
                synced: false
              });

              syncResults.farms.failed++;
            }
          }
        } catch (error) {
          console.error(`Error syncing farm ${farm.id}:`, error);

          // Update farm record with error info
          try {
            await db.farms.update(farm.id, {
              syncError: error.message || 'Error during sync',
              syncAttempted: true,
              lastSyncAttempt: new Date().toISOString(),
              synced: false
            });
          } catch (updateError) {
            console.error(`Error updating farm error status:`, updateError);
          }

          syncResults.farms.failed++;
        }
      }

      // Process forms with similar file handling
      for (const form of unsyncedData.forms) {
        try {
          // Find all file keys in the form data
          const fileFields = await this.findFileKeysInData(form.data);

          if (fileFields.length > 0) {
            syncResults.files.total += fileFields.length;

            // Make a copy of the form data to modify
            let updatedFormData = { ...form.data };
            const uploadedFiles = [];

            // Upload each file and get its URL
            for (const { field, fileKey, file } of fileFields) {
              try {
                // Track this file key
                allFileKeys.add(fileKey);

                // Upload the file to the server
                const uploadedUrl = await this.uploadFileToServer(file, field);

                // Add to the list of successfully uploaded files
                uploadedFiles.push({ field, fileKey, uploadedUrl });

                syncResults.files.synced++;
              } catch (uploadError) {
                console.error(
                  `Error uploading file ${fileKey} for field ${field}:`,
                  uploadError
                );
                syncResults.files.failed++;
              }
            }

            // Replace file keys with uploaded URLs in the form data
            if (uploadedFiles.length > 0) {
              updatedFormData = this.replaceFileKeysWithUrls(
                updatedFormData,
                uploadedFiles.map(f => ({
                  field: f.field,
                  uploadedUrl: f.uploadedUrl
                }))
              );
            }

            // Submit the form with updated data
            const response = await apiService.submitForm(
              form.formType,
              updatedFormData
            );

            if (response && response.success === true) {
              await this.markAsSyncedAndRemove('formSubmissions', form.id);

              // Delete successfully uploaded files
              for (const { fileKey } of uploadedFiles) {
                if (!allFileKeys.has(fileKey)) continue; // Skip if used by other entries

                try {
                  await this.deleteFile(fileKey);
                } catch (deleteError) {
                  console.error(`Error deleting file ${fileKey}:`, deleteError);
                }
              }

              syncResults.forms.synced++;
            } else {
              // Error handling
              syncResults.forms.failed = (syncResults.forms.failed || 0) + 1;
            }
          } else {
            // No file references, use normal sync
            const response = await apiService.submitForm(
              form.formType,
              form.data
            );

            if (response && response.success === true) {
              await this.markAsSyncedAndRemove('formSubmissions', form.id);
              syncResults.forms.synced++;
            } else {
              // Error handling
              syncResults.forms.failed = (syncResults.forms.failed || 0) + 1;
            }
          }
        } catch (error) {
          console.error(`Error syncing form ${form.id}:`, error);
          syncResults.forms.failed = (syncResults.forms.failed || 0) + 1;
        }
      }

      return {
        success: true,
        syncResults,
        farmerIdMappings: Object.fromEntries(farmerIdMappings)
      };
    } catch (error) {
      console.error('Sync operation failed:', error);
      return {
        success: false,
        message: error.message
      };
    }
  },
  // Update farm record with sync error information
  // Update farm record with sync error information
  async updateFarmSyncError(id, errorMessage) {
    try {
      console.log(`Updating sync error for farm with ID: ${id}`, errorMessage);

      // Get the existing farm record
      const existingFarm = await db.farms.get(id);

      if (!existingFarm) {
        console.error(`No farm found with ID: ${id}`);
        return false;
      }

      // Update the farm record with error information
      const updatedFarm = {
        ...existingFarm,
        syncError: errorMessage,
        syncAttempted: true,
        lastSyncAttempt: new Date().toISOString(),
        synced: false
      };

      // Update in the database
      const result = await db.farms.update(id, updatedFarm);

      if (result === 0) {
        console.error(`Update operation affected 0 records for farm ID: ${id}`);
        return false;
      }

      console.log(`Successfully updated sync error for farm ID: ${id}`);
      return true;
    } catch (error) {
      console.error(`Error updating farm sync error:`, error);
      return false;
    }
  },

  async getFarmersPaginated(limit = 20, offset = 0) {
    try {
      return await db.farmers
        .orderBy('first_name') // Or any other field you want to sort by
        .offset(offset)
        .limit(limit)
        .toArray();
    } catch (error) {
      console.error('Error getting paginated farmers:', error);
      throw error;
    }
  },

  // Get total count of farmers
  async getFarmersCount() {
    try {
      return await db.farmers.count();
    } catch (error) {
      console.error('Error getting farmers count:', error);
      throw error;
    }
  },

  // Search farmers by name or phone number
  async searchFarmers(searchTerm, limit = 20, offset = 0) {
    try {
      searchTerm = searchTerm.toLowerCase();

      // In a real implementation, you'd probably want to use indices
      // This is a simplified version that will work but might be slow for large datasets
      const allFarmers = await db.farmers.toArray();

      const filteredFarmers = allFarmers.filter(farmer => {
        const firstName = (farmer.first_name || '').toLowerCase();
        const lastName = (farmer.last_name || '').toLowerCase();
        const phoneNumber = (
          farmer.mobile_number ||
          farmer.phone_number ||
          farmer.phone ||
          ''
        ).toLowerCase();

        return (
          firstName.includes(searchTerm) ||
          lastName.includes(searchTerm) ||
          phoneNumber.includes(searchTerm)
        );
      });

      // Manual pagination of results
      return filteredFarmers.slice(offset, offset + limit);
    } catch (error) {
      console.error('Error searching farmers:', error);
      throw error;
    }
  },

  // Get count of search results
  async searchFarmersCount(searchTerm) {
    try {
      searchTerm = searchTerm.toLowerCase();

      const allFarmers = await db.farmers.toArray();

      const filteredFarmers = allFarmers.filter(farmer => {
        const firstName = (farmer.first_name || '').toLowerCase();
        const lastName = (farmer.last_name || '').toLowerCase();
        const phoneNumber = (
          farmer.mobile_number ||
          farmer.phone_number ||
          farmer.phone ||
          ''
        ).toLowerCase();

        return (
          firstName.includes(searchTerm) ||
          lastName.includes(searchTerm) ||
          phoneNumber.includes(searchTerm)
        );
      });

      return filteredFarmers.length;
    } catch (error) {
      console.error('Error getting search count:', error);
      throw error;
    }
  },

  // Optimized version of searchFarmers using index if your DB supports it
  // Note: You need to create appropriate indexes in your schema
  async searchFarmersOptimized(searchTerm, limit = 20, offset = 0) {
    try {
      searchTerm = searchTerm.toLowerCase();

      // Using compound index would be much more efficient
      // This assumes you have created appropriate indexes in your schema
      const byFirstName = await db.farmers
        .where('first_name')
        .startsWithIgnoreCase(searchTerm)
        .toArray();

      const byLastName = await db.farmers
        .where('last_name')
        .startsWithIgnoreCase(searchTerm)
        .toArray();

      const byPhone = await db.farmers
        .where('mobile_number')
        .startsWithIgnoreCase(searchTerm)
        .toArray();

      // Combine results and remove duplicates
      const combined = [...byFirstName];

      byLastName.forEach(farmer => {
        if (!combined.some(f => f.id === farmer.id)) {
          combined.push(farmer);
        }
      });

      byPhone.forEach(farmer => {
        if (!combined.some(f => f.id === farmer.id)) {
          combined.push(farmer);
        }
      });

      // Sort results
      combined.sort((a, b) => {
        const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim();
        const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim();
        return nameA.localeCompare(nameB);
      });

      // Manual pagination
      return combined.slice(offset, offset + limit);
    } catch (error) {
      console.error('Error in optimized search:', error);
      throw error;
    }
  },

  // Save cultivation data
  async saveCultivation(cultivationData) {
    try {
      const now = new Date().toISOString();

      // Ensure synced is explicitly set to false
      const dataToSave = {
        ...cultivationData,
        createdAt: now,
        updatedAt: now,
        synced: false,
        syncAttempted: false,
        syncError: null
      };

      const id = await db.cultivations.add(dataToSave);
      console.log(`Cultivation saved with ID: ${id}`, dataToSave);

      // Dispatch event to notify of database change
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));

      return id;
    } catch (error) {
      console.error('Error saving cultivation:', error);
      throw error;
    }
  },

  // Get cultivation by ID
  async getCultivationById(id) {
    try {
      console.log(`Getting cultivation with ID: ${id}`);

      // Try both the original ID and parsed ID to ensure we find the record
      let cultivation = null;

      // First try with the ID as-is
      cultivation = await db.cultivations.get(id);

      // If not found and ID is string, try parsing it as integer
      if (!cultivation && typeof id === 'string' && !isNaN(id)) {
        const numericId = parseInt(id, 10);
        console.log(`Trying again with parsed numeric ID: ${numericId}`);
        cultivation = await db.cultivations.get(numericId);
      }

      // If still not found and ID is numeric, try as string
      if (!cultivation && typeof id === 'number') {
        const stringId = id.toString();
        console.log(`Trying again with string ID: ${stringId}`);
        cultivation = await db.cultivations.get(stringId);
      }

      if (!cultivation) {
        console.warn(`No cultivation found with ID: ${id}`);
        return null;
      }

      console.log(`Found cultivation:`, cultivation);
      return cultivation;
    } catch (error) {
      console.error(`Error getting cultivation by ID:`, error);
      throw error;
    }
  },

  // Get cultivations by farm ID
  async getCultivationsByFarmId(farmId) {
    try {
      console.log(`Looking for cultivations with farm ID: ${farmId}`);

      // Use toArray and filter to find matching cultivations
      const allCultivations = await db.cultivations.toArray();

      // Manually filter the cultivations by farm ID
      const matchingCultivations = allCultivations.filter(cultivation => {
        // Convert both to strings for comparison to avoid type issues
        const cultivationFarmId = cultivation.farm
          ? cultivation.farm.toString()
          : null;
        const searchFarmId = farmId.toString();

        return cultivationFarmId === searchFarmId;
      });

      console.log(
        `Found ${matchingCultivations.length} cultivations for farm ${farmId}`
      );
      return matchingCultivations;
    } catch (error) {
      console.error(`Error getting cultivations for farm ${farmId}:`, error);
      return [];
    }
  },

  // Update an existing cultivation
  async updateCultivation(id, cultivationData) {
    try {
      console.log(`Updating cultivation with ID: ${id}`, cultivationData);

      // Attempt to handle both numeric and string IDs
      let cultivationId;

      // Try to convert to integer if it's a string containing a number
      if (typeof id === 'string' && !isNaN(id)) {
        cultivationId = parseInt(id, 10);
      } else {
        cultivationId = id;
      }

      // First check if the cultivation exists
      const existingCultivation = await db.cultivations.get(cultivationId);

      if (!existingCultivation) {
        console.error(`No cultivation found with ID: ${cultivationId}`);
        throw new Error(`No cultivation found with ID: ${id}`);
      }

      // Make sure synced status remains false for offline edits
      const updatedData = {
        ...cultivationData,
        synced: false,
        updatedAt: new Date().toISOString(),
        // Safely check if these properties exist and provide defaults
        syncError:
          existingCultivation.syncError !== undefined
            ? existingCultivation.syncError
            : null,
        syncAttempted:
          existingCultivation.syncAttempted !== undefined
            ? existingCultivation.syncAttempted
            : false,
        lastSyncAttempt:
          existingCultivation.lastSyncAttempt !== undefined
            ? existingCultivation.lastSyncAttempt
            : null
      };

      // Update in the database
      const result = await db.cultivations.update(cultivationId, updatedData);

      if (result === 0) {
        console.error(
          `Update operation affected 0 records for ID: ${cultivationId}`
        );
        throw new Error(
          `Failed to update cultivation with ID: ${cultivationId}`
        );
      }

      console.log(
        `Successfully updated cultivation with ID: ${cultivationId}, affected records: ${result}`
      );

      // Dispatch event to notify of database change
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));

      return cultivationId;
    } catch (error) {
      console.error(`Error updating cultivation:`, error);
      throw error;
    }
  },

  // Delete a cultivation record
  async deleteCultivation(id) {
    try {
      console.log(`Deleting cultivation with ID: ${id}`);
      await db.cultivations.delete(id);
      console.log(`Cultivation with ID: ${id} deleted successfully`);

      // Dispatch event to notify of database change
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));

      return true;
    } catch (error) {
      console.error(`Error deleting cultivation with ID ${id}:`, error);
      throw error;
    }
  },

  // Update cultivation sync error information
  async updateCultivationSyncError(id, errorMessage) {
    try {
      console.log(
        `Updating sync error for cultivation with ID: ${id}`,
        errorMessage
      );

      // Get the existing cultivation record
      const existingCultivation = await db.cultivations.get(id);

      if (!existingCultivation) {
        console.error(`No cultivation found with ID: ${id}`);

        // Try with a different ID format (number vs string)
        let altId = id;
        if (typeof id === 'string' && !isNaN(id)) {
          altId = parseInt(id, 10);
        } else if (typeof id === 'number') {
          altId = id.toString();
        }

        const altCultivation = await db.cultivations.get(altId);
        if (!altCultivation) {
          console.error(
            `Also no cultivation found with alternate ID: ${altId}`
          );
          return false;
        }

        // Use the alternate ID if it worked
        id = altId;
        console.log(`Found cultivation with alternate ID: ${id}`);
      }

      // Update the cultivation record with error information
      const updatedCultivation = {
        ...existingCultivation,
        syncError: errorMessage,
        syncAttempted: true,
        lastSyncAttempt: new Date().toISOString(),
        synced: false
      };

      // Update in the database
      const result = await db.cultivations.update(id, updatedCultivation);

      if (result === 0) {
        console.error(
          `Update operation affected 0 records for cultivation ID: ${id}`
        );
        return false;
      }

      console.log(`Successfully updated sync error for cultivation ID: ${id}`);

      // Dispatch event to notify of database changes
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));

      return true;
    } catch (error) {
      console.error(`Error updating cultivation sync error:`, error);
      return false;
    }
  },

  // Get all cultivations
  async getAllCultivations() {
    try {
      const cultivations = await db.cultivations.toArray();
      console.log(`Retrieved ${cultivations.length} cultivations`);
      return cultivations;
    } catch (error) {
      console.error('Error getting cultivations:', error);
      return [];
    }
  },

  async clearAllExceptMapTiles() {
    try {
      console.log('Starting to clear all data except map tiles');

      // Clear each table individually to avoid transaction errors with non-existent tables
      try {
        await this.farmers.clear();
        console.log('Cleared farmers table');
      } catch (error) {
        console.error('Error clearing farmers table:', error);
      }

      try {
        await this.farms.clear();
        console.log('Cleared farms table');
      } catch (error) {
        console.error('Error clearing farms table:', error);
      }

      try {
        await this.formSubmissions.clear(); // Correct name (not 'forms')
        console.log('Cleared formSubmissions table');
      } catch (error) {
        console.error('Error clearing formSubmissions table:', error);
      }

      try {
        await this.formDefinitions.clear();
        console.log('Cleared formDefinitions table');
      } catch (error) {
        console.error('Error clearing formDefinitions table:', error);
      }

      try {
        await this.fileStorage.clear(); // Correct name (not 'files')
        console.log('Cleared fileStorage table');
      } catch (error) {
        console.error('Error clearing fileStorage table:', error);
      }

      try {
        await this.dropdownOptions.clear();
        console.log('Cleared dropdownOptions table');
      } catch (error) {
        console.error('Error clearing dropdownOptions table:', error);
      }

      try {
        await this.appConfig.clear();
        console.log('Cleared appConfig table');
      } catch (error) {
        console.error('Error clearing appConfig table:', error);
      }

      // Dispatch event to notify of database changes
      window.dispatchEvent(new CustomEvent('dexieDbChanged'));

      console.log('Successfully cleared all data except map tiles');
      return true;
    } catch (error) {
      console.error('Error clearing data except map tiles:', error);
      throw error;
    }
  },

  /**
   * Get map tile storage information
   * @returns {Promise<Object>} Object with tile count and total size
   */
  async getMapTileInfo() {
    try {
      const tiles = await db.mapTiles.toArray();
      let totalSize = 0;

      tiles.forEach(tile => {
        // Estimate size from data URL length (base64 is ~33% larger than binary)
        totalSize += tile.data.length * 0.75;
      });

      return {
        tileCount: tiles.length,
        size: totalSize
      };
    } catch (error) {
      console.error('Error getting map tile info:', error);
      return { tileCount: 0, size: 0 };
    }
  },

  /**
   * Clear all map tiles from the database
   * @returns {Promise<boolean>} Success status
   */
  async clearMapTiles() {
    try {
      await db.mapTiles.clear();
      console.log('Cleared all map tiles');
      return true;
    } catch (error) {
      console.error('Error clearing map tiles:', error);
      throw error;
    }
  },

  async saveMapTile(tileData) {
    try {
      const id = await db.mapTiles.put(tileData);
      return id;
    } catch (error) {
      console.error('Error saving map tile:', error);
      throw error;
    }
  },

  async getMapTile(url) {
    try {
      return await db.mapTiles.get(url);
    } catch (error) {
      console.error('Error getting map tile:', error);
      return null;
    }
  },

  /**
   * Check if the app has enough map tiles downloaded for offline use
   * @param {number} minimumTiles - Minimum number of tiles required
   * @returns {Promise<boolean>} Whether enough tiles are available
   */
  async hasEnoughMapTiles(minimumTiles = 100) {
    try {
      const count = await db.mapTiles.count();
      return count >= minimumTiles;
    } catch (error) {
      console.error('Error checking map tiles count:', error);
      return false;
    }
  }
};

const getDb = () => {
  // Import from 'offlineDBService.js'
  const db = offlineDBService.db; // Access the db instance
  return db;
};

// Initialize the database
offlineDBService.getDb = getDb;
offlineDBService.init();

export default offlineDBService;
